/**
 * Conversation outbox — a durable, local staging buffer for conversations
 * whose backend persistence has not yet been confirmed.
 *
 * Why this exists: a new conversation's only durable write is a backgrounded,
 * non-blocking POST (App.tsx) that the browser will *abort* on refresh and
 * that fails silently on a transient backend blip. When that happens the
 * conversation lives only in the React Query cache, so a refresh loses it.
 *
 * The outbox closes that gap. Every saved turn is mirrored into localStorage
 * BEFORE the network write. On success the entry is cleared; on failure (or an
 * aborting refresh) it survives, and `reconcileOutbox` replays it to the
 * backend on the next load. The backend's `POST /conversations` is an
 * idempotent upsert (ON CONFLICT (id) DO UPDATE … message_count >=), so
 * replaying is always safe — never a duplicate, never a downgrade.
 *
 * All reads/writes are defensive: localStorage being unavailable, full, or
 * holding corrupt JSON must never throw into the chat flow.
 */

import type { Conversation } from '@/types/chat.types';

export const OUTBOX_STORAGE_KEY = 'stellar_conversation_outbox';

/**
 * Hard cap on staged entries so a long-running, offline-ish session can't grow
 * localStorage without bound. The newest entries win; the oldest are evicted.
 */
export const OUTBOX_MAX_ENTRIES = 50;

function readRaw(): Conversation[] {
  try {
    const raw = localStorage.getItem(OUTBOX_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Conversation[]) : [];
  } catch {
    // Corrupt payload or no localStorage — treat as empty rather than throw.
    return [];
  }
}

function writeRaw(entries: Conversation[]): void {
  try {
    localStorage.setItem(OUTBOX_STORAGE_KEY, JSON.stringify(entries));
  } catch (error) {
    // Quota exceeded / storage disabled. Durability is best-effort; never
    // let a staging failure break the send.
    console.warn(
      '[conversationOutbox] Failed to persist outbox:',
      error instanceof Error ? error.message : error
    );
  }
}

/**
 * JSON round-trips Date fields to ISO strings. Revive them so callers get a
 * Conversation that honours its declared `Date` types.
 */
function reviveConversation(conversation: Conversation): Conversation {
  return {
    ...conversation,
    createdAt: new Date(conversation.createdAt),
    updatedAt: new Date(conversation.updatedAt),
    messages: Array.isArray(conversation.messages)
      ? conversation.messages.map((message) => ({
          ...message,
          timestamp: new Date(message.timestamp),
        }))
      : [],
  };
}

/**
 * Stage (or re-stage) a conversation. Upserts by id so the latest snapshot of a
 * given conversation always wins, then trims to the newest OUTBOX_MAX_ENTRIES.
 */
export function stageConversation(conversation: Conversation): void {
  const entries = readRaw().filter((c) => c.id !== conversation.id);
  entries.push(conversation);
  // Keep the newest N; drop the oldest from the front.
  writeRaw(entries.slice(-OUTBOX_MAX_ENTRIES));
}

/** Remove a conversation from the outbox once its backend write is confirmed. */
export function markConversationSynced(id: string): void {
  const entries = readRaw();
  const next = entries.filter((c) => c.id !== id);
  if (next.length !== entries.length) {
    writeRaw(next);
  }
}

/** All still-unconfirmed conversations belonging to `userId`. */
export function getPendingConversations(userId: string): Conversation[] {
  return readRaw()
    .filter((c) => c.userId === userId)
    .map(reviveConversation);
}

/** Drop the entire outbox (sign-out / tests). */
export function clearOutbox(): void {
  try {
    localStorage.removeItem(OUTBOX_STORAGE_KEY);
  } catch {
    // ignore — nothing to clean up if storage is unavailable
  }
}

/**
 * Replay every pending conversation for `userId` through `save` (the storage
 * adapter's idempotent create/upsert). Each success clears its entry; each
 * failure is left staged to retry on the next load and does not block the
 * others. Returns the number actually reconciled so the caller can decide
 * whether to refresh the conversation list.
 */
export async function reconcileOutbox(
  save: (conversation: Conversation) => Promise<void>,
  userId: string
): Promise<number> {
  const pending = getPendingConversations(userId);
  let synced = 0;
  for (const conversation of pending) {
    try {
      await save(conversation);
      markConversationSynced(conversation.id);
      synced++;
    } catch (error) {
      console.warn(
        '[conversationOutbox] Reconcile failed for',
        conversation.id,
        error instanceof Error ? error.message : error
      );
      // Leave it staged; the next load will retry.
    }
  }
  return synced;
}
